/**
 * FileUpload Component
 *
 * S3 file upload with preview.
 */
import { useState, useRef, useCallback } from 'react';
import { useUpload } from '../../hooks/useUpload';

interface FileUploadProps {
  value: string;
  onChange: (url: string) => void;
  accept?: string;
  maxSizeMB?: number;
  label?: string;
  error?: string;
}

const DEFAULT_ACCEPT = 'image/png,image/jpeg,image/jpg,image/gif,image/webp,image/svg+xml';
const DEFAULT_MAX_SIZE_MB = 5;

export const FileUpload = ({
  value,
  onChange,
  accept = DEFAULT_ACCEPT,
  maxSizeMB = DEFAULT_MAX_SIZE_MB,
  label,
  error: externalError,
}: FileUploadProps) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [preview, setPreview] = useState<string>(value || '');
  const [localError, setLocalError] = useState<string | null>(null);
  const { isUploading, progress, error: uploadError, upload, reset } = useUpload();

  const error = externalError || localError || uploadError;

  const handleFileSelect = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;

      setLocalError(null);
      reset();

      // Validate file type
      const acceptedTypes = accept.split(',').map((t) => t.trim());
      if (!acceptedTypes.includes(file.type)) {
        setLocalError('Invalid file type. Please select an image.');
        return;
      }

      // Validate file size
      const maxSizeBytes = maxSizeMB * 1024 * 1024;
      if (file.size > maxSizeBytes) {
        setLocalError(`File too large. Maximum size is ${maxSizeMB}MB.`);
        return;
      }

      // Show local preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setPreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);

      // Upload to S3
      const fileUrl = await upload(file);
      if (fileUrl) {
        setPreview(fileUrl);
        onChange(fileUrl);
      }
    },
    [accept, maxSizeMB, upload, onChange, reset]
  );

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  const handleClear = () => {
    setPreview('');
    onChange('');
    setLocalError(null);
    reset();
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="mb-4">
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {label}
        </label>
      )}

      <div className="flex items-start gap-4">
        {/* Preview */}
        <div
          className={`
            relative w-24 h-24 rounded-lg border-2 border-dashed
            flex items-center justify-center bg-gray-50 overflow-hidden
            ${error ? 'border-red-300' : 'border-gray-300'}
          `}
        >
          {preview ? (
            <>
              <img
                src={preview}
                alt="Preview"
                className="w-full h-full object-contain"
              />
              {isUploading && (
                <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                  <div className="text-white text-sm font-medium">{progress}%</div>
                </div>
              )}
            </>
          ) : (
            <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
              />
            </svg>
          )}
        </div>

        {/* Controls */}
        <div className="flex-1">
          <input
            ref={fileInputRef}
            type="file"
            accept={accept}
            onChange={handleFileSelect}
            className="hidden"
          />

          <div className="flex flex-col gap-2">
            <button
              type="button"
              onClick={handleClick}
              disabled={isUploading}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-metrica-green disabled:opacity-50"
            >
              {isUploading ? 'Uploading...' : preview ? 'Change File' : 'Choose File'}
            </button>

            {preview && (
              <button
                type="button"
                onClick={handleClear}
                disabled={isUploading}
                className="px-4 py-2 text-sm font-medium text-red-600 bg-white border border-red-300 rounded-lg hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50"
              >
                Remove
              </button>
            )}
          </div>

          <p className="mt-2 text-xs text-gray-500">
            PNG, JPG, GIF, WebP or SVG. Max {maxSizeMB}MB.
          </p>
        </div>
      </div>

      {error && (
        <p className="mt-1 text-sm text-red-600">{error}</p>
      )}
    </div>
  );
};

export default FileUpload;
